/**
 * CORE-1999 — stable identity of a failed backend request, for GlitchTip.
 *
 * `api.ts` throws the parsed error body (ASP.NET ProblemDetails) with `status`
 * set. `captureError` used to JSON.stringify that body into the event title, and
 * the body carries a per-request `traceId` — so every event opened a new issue
 * and fired the «new error» alert (GlitchTip 32/67/73/79/87/96: one endpoint,
 * one status, eight groups). Grouping must instead follow the request:
 * method + path template + status.
 *
 * `api.ts` attaches the request it made as a NON-enumerable `apiRequest`
 * property, so it never leaks into spreads, `toEqual` or JSON (the raw URL may
 * carry a query string).
 */

export type ApiRequestInfo = { method?: string; url?: string };

export type ApiErrorIdentity = {
  status: number;
  method?: string;
  /** Path with ids / codes replaced by placeholders: `/api/watch-parties/id/{id}`. */
  path?: string;
  /** Short, low-cardinality title — never contains traceId or ids. */
  title: string;
};

const GUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

function templateSegment(segment: string): string {
  if (segment === '') return segment;
  let s = segment;
  try {
    s = decodeURIComponent(segment);
  } catch {
    // keep the raw segment
  }
  if (/^\d+$/.test(s) || GUID.test(s)) return '{id}';
  // Invite codes (WAS7HJ), hashes, `user_123` style keys: a digit and no hyphen
  // (route words like `hub-ticket` / `watch-parties` have hyphens, not digits).
  if (s.length >= 4 && /\d/.test(s) && /^[A-Za-z0-9_.]+$/.test(s)) return '{key}';
  // All-caps invite code without digits (ABCDEF).
  if (/^[A-Z]{4,}$/.test(s)) return '{key}';
  // Anything outside a route word alphabet — emails, spaces, slugs with unicode.
  if (/[^A-Za-z0-9._-]/.test(s)) return '{key}';
  return segment;
}

/** `https://api.x/api/watch-parties/id/1005013?x=1` → `/api/watch-parties/id/{id}`. */
export function apiPathTemplate(url: string | null | undefined): string | undefined {
  if (!url || typeof url !== 'string') return undefined;
  let path = url.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/?#]*/i, '');
  path = path.split(/[?#]/)[0] || '/';
  return path.split('/').map(templateSegment).join('/');
}

/**
 * Identity of an HTTP error thrown by `api.ts`, or null when `error` is not
 * one (Error instances, network failures, objects without a numeric status).
 */
export function describeApiError(error: unknown): ApiErrorIdentity | null {
  if (!error || typeof error !== 'object' || error instanceof Error) return null;
  const status = (error as { status?: unknown }).status;
  if (typeof status !== 'number') return null;
  const request = (error as { apiRequest?: ApiRequestInfo }).apiRequest;
  const method = typeof request?.method === 'string' ? request.method.toUpperCase() : undefined;
  const path = apiPathTemplate(request?.url);
  // ProblemDetails `title` («Not Found») is a constant per status; free-form
  // `message` / `detail` may carry ids, so it stays out of the title.
  const problemTitle = (error as { title?: unknown }).title;
  const suffix =
    typeof problemTitle === 'string' && problemTitle.length > 0 && problemTitle.length <= 60 && !/\d/.test(problemTitle)
      ? ` ${problemTitle}`
      : '';
  const target = method && path ? `${method} ${path}` : path ?? 'HTTP';
  return { status, method, path, title: `${target} → ${status}${suffix}` };
}
